import React, { useEffect, useMemo, useState } from "react";
import { Modal, PrimaryButton, SecondaryButton } from "../../UI/DesignStandards";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import { supabase } from "../../../supabaseClient";
import {
  matchReasonLabel,
  searchOrgBowlersForTeamCreate,
} from "../../../utility/bowlerMatch";

const mergeErrorMessage = (error) => {
  const message = error?.message || "";
  if (
    error?.code === "PGRST202" ||
    /could not find the function/i.test(message) ||
    /preview_bowler_merge|merge_bowlers/i.test(message)
  ) {
    return "Merge is not set up on this database yet.";
  }
  return message || "Could not merge bowlers.";
};

const averageText = (value, hasAverage) => {
  if (hasAverage === false || value == null || value === "") return "No average";
  return String(value);
};

const teamLabel = (name) => name || "Team";

const previewLines = (preview) => {
  if (!preview) return [];
  const lines = [];
  const moves = preview.moves || {};
  const labels = [
    ["scores", "Scores"],
    ["eventEntry", "Event entries"],
    ["tournamentAverages", "Tournament averages"],
    ["teamAssignments", "Team assignments"],
    ["contacts", "Contacts"],
    ["signupMatches", "Signup matches"],
    ["scoreHistory", "Score history rows"],
    ["bracketTickets", "Bracket tickets"],
  ];
  labels.forEach(([key, label]) => {
    const count = Number(moves[key] || 0);
    if (count > 0) lines.push(`${label} that move: ${count}`);
  });

  if (preview.fills?.usbcID) {
    lines.push(`USBC ID ${preview.fills.usbcID} is copied onto the bowler you keep.`);
  }

  const usbc = preview.conflicts?.usbcID;
  if (usbc?.kept) {
    lines.push(
      `USBC ID stays ${usbc.kept}. ${usbc.absorbed} is not copied.`
    );
  }

  (preview.conflicts?.tournamentAverages || []).forEach((row) => {
    const kept = averageText(row.keptAverage, row.keptHasAverage);
    const dropped = averageText(row.droppedAverage, row.droppedHasAverage);
    lines.push(
      `${row.tournamentName || "Tournament"}: keep average ${kept}. The other average (${dropped}) is removed.`
    );
  });

  (preview.conflicts?.contacts || []).forEach((row) => {
    const contactType = String(row.type || "").toLowerCase();
    const type =
      contactType === "phone" ? "Phone" : contactType === "email" ? "Email" : "Contact";
    lines.push(`${type}: keep ${row.kept}. ${row.absorbed} is also kept.`);
  });

  (preview.collapsed?.teamAssignments || []).forEach((row) => {
    lines.push(
      `${teamLabel(row.teamName)} is already on the bowler you keep. The extra team row is removed. Scores stay.`
    );
  });

  (preview.collapsed?.eventEntries || []).forEach((row) => {
    lines.push(
      `${row.eventName || "Event"} on ${teamLabel(row.teamName)} is already on the bowler you keep. The extra entry is removed.`
    );
  });

  (preview.collapsed?.scores || []).forEach((row) => {
    const game =
      row.gameNumber == null ? "A game" : `Game ${row.gameNumber}`;
    const team = teamLabel(row.teamName);
    if (row.keptScore == null && row.droppedScore != null) {
      lines.push(
        `${game} on ${team}: score ${row.droppedScore} moves. The empty score is removed.`
      );
      return;
    }
    if (
      row.keptScore != null &&
      row.droppedScore != null &&
      row.keptScore !== row.droppedScore
    ) {
      lines.push(
        `${game} on ${team}: keep ${row.keptScore}. The other score (${row.droppedScore}) is removed.`
      );
      return;
    }
    lines.push(`${game} on ${team}: one score stays. The extra row is removed.`);
  });

  if (lines.length === 0) {
    lines.push("No scores, entries, or contacts to move.");
  }
  return lines;
};

const BowlerPick = ({
  label,
  hint,
  selected,
  query,
  onQuery,
  onSelect,
  bowlers,
  excludeId,
}) => {
  const results = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed || selected) return [];
    return searchOrgBowlersForTeamCreate({
      query: trimmed,
      usbcID: trimmed,
      orgBowlers: bowlers,
      excludeIds: excludeId ? [excludeId] : [],
      limit: 6,
    });
  }, [bowlers, excludeId, query, selected]);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-brand-charcoal">{label}</p>
      <p className="text-sm text-brand-muted">{hint}</p>
      {selected ? (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-brand-charcoal/15 px-3 py-2">
          <div>
            <div className="font-medium text-brand-charcoal">{selected.name}</div>
            <div className="text-sm text-brand-muted">
              {selected.usbcID ? `USBC ID: ${selected.usbcID}` : "No USBC ID"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-sm text-brand-gold-ink hover:underline"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder="Search Bowlers"
            className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
          />
          {results.length > 0 ? (
            <div className="border border-brand-charcoal/15 rounded-lg overflow-hidden">
              {results.map((bowler) => {
                const reason = matchReasonLabel(bowler.matchReason);
                return (
                  <button
                    type="button"
                    key={bowler.id}
                    onClick={() => onSelect(bowler)}
                    className="w-full px-3 py-2 text-left hover:bg-brand-cream border-b border-brand-charcoal/10 last:border-b-0"
                  >
                    <div className="font-medium text-brand-charcoal">{bowler.name}</div>
                    <div className="text-sm text-brand-muted">
                      {bowler.usbcID ? `USBC ID: ${bowler.usbcID}` : "No USBC ID"}
                      {reason ? ` · ${reason}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const MergeBowlersModal = ({
  isOpen,
  bowlers,
  initialKeepId,
  initialAbsorbId,
  onClose,
  onMerged,
}) => {
  const [keepId, setKeepId] = useState(initialKeepId || null);
  const [absorbId, setAbsorbId] = useState(initialAbsorbId || null);
  const [keepQuery, setKeepQuery] = useState("");
  const [absorbQuery, setAbsorbQuery] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setKeepId(initialKeepId || null);
    setAbsorbId(initialAbsorbId || null);
    setKeepQuery("");
    setAbsorbQuery("");
    setReason("");
    setPreview(null);
    setPreviewError(null);
    setMergeError(null);
  }, [isOpen, initialKeepId, initialAbsorbId]);

  useEffect(() => {
    if (!isOpen || !keepId || !absorbId || keepId === absorbId) {
      setPreview(null);
      setPreviewError(keepId && absorbId && keepId === absorbId
        ? "Choose two different bowlers."
        : null);
      setPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setMergeError(null);

    supabase
      .rpc("preview_bowler_merge", {
        p_source_bowler_id: absorbId,
        p_target_bowler_id: keepId,
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setPreview(null);
          setPreviewError(mergeErrorMessage(error));
        } else {
          setPreview(data);
        }
        setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [absorbId, isOpen, keepId]);

  const byId = useMemo(() => {
    const map = new Map();
    (bowlers || []).forEach((bowler) => map.set(bowler.id, bowler));
    return map;
  }, [bowlers]);

  const keep = byId.get(keepId) || null;
  const absorb = byId.get(absorbId) || null;
  const lines = previewLines(preview);
  const canMerge = Boolean(
    keepId &&
      absorbId &&
      keepId !== absorbId &&
      preview &&
      !previewError &&
      !previewLoading &&
      !merging
  );

  const handleMerge = async () => {
    if (!canMerge) return;
    setMerging(true);
    setMergeError(null);
    const { error } = await supabase.rpc("merge_bowlers", {
      p_source_bowler_id: absorbId,
      p_target_bowler_id: keepId,
      p_merge_reason: reason.trim() || null,
    });
    setMerging(false);
    if (error) {
      setMergeError(mergeErrorMessage(error));
      return;
    }
    onMerged({
      absorbId,
      keepId,
      usbcID: preview?.fills?.usbcID || null,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={merging ? () => {} : onClose}
      title="Merge bowlers"
      subtitle="Scores, entries, and contacts move onto the bowler you keep."
      maxWidth="max-w-2xl"
      showCloseButton={!merging}
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <SecondaryButton type="button" onClick={onClose} disabled={merging}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleMerge} disabled={!canMerge}>
            {merging ? "Merging..." : "Merge bowlers"}
          </PrimaryButton>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex justify-end">
          <button
            type="button"
            className="text-sm text-brand-gold-ink hover:underline disabled:opacity-50"
            disabled={!keepId && !absorbId}
            onClick={() => {
              setKeepId(absorbId);
              setAbsorbId(keepId);
              setKeepQuery("");
              setAbsorbQuery("");
            }}
          >
            Swap keep and absorb
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BowlerPick
            label="Keep"
            hint="This bowler stays."
            selected={keep}
            query={keepQuery}
            onQuery={setKeepQuery}
            onSelect={(bowler) => {
              setKeepId(bowler?.id || null);
              setKeepQuery("");
            }}
            bowlers={bowlers}
            excludeId={absorbId}
          />
          <BowlerPick
            label="Absorb"
            hint="This bowler is removed after the merge."
            selected={absorb}
            query={absorbQuery}
            onQuery={setAbsorbQuery}
            onSelect={(bowler) => {
              setAbsorbId(bowler?.id || null);
              setAbsorbQuery("");
            }}
            bowlers={bowlers}
            excludeId={keepId}
          />
        </div>

        {previewLoading ? (
          <p className="text-sm text-brand-muted">Checking what will move...</p>
        ) : null}
        {previewError ? <InlineAlert type="error" message={previewError} /> : null}
        {preview && !previewLoading ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-brand-charcoal">What will move</p>
            <ul className="text-sm text-brand-charcoal space-y-1 list-disc pl-5">
              {lines.map((line, index) => (
                <li key={`${index}-${line}`}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div>
          <label
            htmlFor="merge-reason"
            className="block text-sm font-medium text-brand-charcoal mb-2"
          >
            Notes
          </label>
          <input
            id="merge-reason"
            type="text"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
          />
        </div>
        {mergeError ? <InlineAlert type="error" message={mergeError} /> : null}
      </div>
    </Modal>
  );
};

export default MergeBowlersModal;
